import React, { useState } from 'react';
import { apiClient } from '../../api/client';
import { ChapterDropdown } from './ChapterDropdown';

interface Tier {
  id: string;
  name: string;
  price: number;
  active: boolean;
}

interface CreateOrderFormProps {
  tiers: Tier[];
  onOrderCreated: () => void;
}

interface OrderFormState {
  referenceId: string;
  purchaserName: string;
  purchaserEmail: string;
  purchaserPhone: string;
  ticketCount: string;
  tierId: string;
  chapter: string;
  chapterFreeText: string;
  isComplimentary: boolean;
  complimentaryReason: string;
}

interface FormErrors {
  referenceId?: string;
  purchaserName?: string;
  purchaserEmail?: string;
  purchaserPhone?: string;
  ticketCount?: string;
  tierId?: string;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const initialFormState: OrderFormState = {
  referenceId: '',
  purchaserName: '',
  purchaserEmail: '',
  purchaserPhone: '',
  ticketCount: '',
  tierId: '',
  chapter: '',
  chapterFreeText: '',
  isComplimentary: false,
  complimentaryReason: '',
};

const styles: Record<string, React.CSSProperties> = {
  formSection: {
    backgroundColor: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '1.25rem',
    marginBottom: '1.5rem',
  },
  formTitle: {
    fontSize: '0.95rem',
    fontWeight: 600,
    color: '#0f2d1f',
    marginBottom: '1rem',
    marginTop: 0,
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '0.75rem',
    marginBottom: '0.75rem',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.25rem',
  },
  formLabel: {
    fontSize: '0.8rem',
    fontWeight: 500,
    color: '#374151',
  },
  formInput: {
    padding: '0.5rem 0.75rem',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    fontSize: '0.85rem',
  },
  formInputError: {
    padding: '0.5rem 0.75rem',
    border: '1px solid #d32f2f',
    borderRadius: '4px',
    fontSize: '0.85rem',
  },
  formSelect: {
    padding: '0.5rem 0.75rem',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    fontSize: '0.85rem',
    backgroundColor: '#fff',
  },
  formSelectError: {
    padding: '0.5rem 0.75rem',
    border: '1px solid #d32f2f',
    borderRadius: '4px',
    fontSize: '0.85rem',
    backgroundColor: '#fff',
  },
  fieldError: {
    fontSize: '0.75rem',
    color: '#d32f2f',
    marginTop: '0.15rem',
  },
  formButton: {
    padding: '0.5rem 1rem',
    backgroundColor: '#0f2d1f',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.85rem',
    fontWeight: 500,
    cursor: 'pointer',
    marginTop: '0.5rem',
  },
  formButtonDisabled: {
    padding: '0.5rem 1rem',
    backgroundColor: '#9ca3af',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.85rem',
    fontWeight: 500,
    cursor: 'not-allowed',
    marginTop: '0.5rem',
  },
  successMessage: {
    color: '#166534',
    backgroundColor: '#dcfce7',
    padding: '0.5rem 0.75rem',
    borderRadius: '4px',
    fontSize: '0.85rem',
    marginTop: '0.75rem',
  },
  formError: {
    color: '#d32f2f',
    backgroundColor: '#fdecea',
    padding: '0.5rem 0.75rem',
    borderRadius: '4px',
    fontSize: '0.85rem',
    marginTop: '0.75rem',
  },
};

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
}

function validateForm(form: OrderFormState): FormErrors {
  const errors: FormErrors = {};

  if (!form.referenceId.trim()) {
    errors.referenceId = 'Reference ID is required.';
  }

  if (!form.purchaserName.trim()) {
    errors.purchaserName = 'Purchaser name is required.';
  }

  if (!form.purchaserEmail.trim()) {
    errors.purchaserEmail = 'Email is required.';
  } else if (!EMAIL_REGEX.test(form.purchaserEmail.trim())) {
    errors.purchaserEmail = 'Please enter a valid email address.';
  }

  if (!form.purchaserPhone.trim()) {
    errors.purchaserPhone = 'Phone number is required.';
  }

  const count = parseInt(form.ticketCount, 10);
  if (!form.ticketCount.trim()) {
    errors.ticketCount = 'Ticket count is required.';
  } else if (isNaN(count) || count < 1 || count > 100) {
    errors.ticketCount = 'Ticket count must be between 1 and 100.';
  }

  if (!form.tierId) {
    errors.tierId = 'Please select a tier.';
  }

  return errors;
}

export function CreateOrderForm({ tiers, onOrderCreated }: CreateOrderFormProps) {
  const [form, setForm] = useState<OrderFormState>(initialFormState);
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [apiError, setApiError] = useState('');

  const activeTiers = tiers.filter((t) => t.active);

  const handleChange = (field: keyof OrderFormState) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    // Clear field error on change
    if (field in errors && errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccessMessage('');
    setApiError('');

    const validationErrors = validateForm(form);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});
    setSubmitting(true);

    try {
      // Resolve chapter affiliation value
      let chapterAffiliation: string | undefined;
      if (form.chapter && form.chapter !== 'Other') {
        chapterAffiliation = form.chapter;
      } else if (form.chapter === 'Other' && form.chapterFreeText.trim()) {
        chapterAffiliation = form.chapterFreeText.trim();
      }

      await apiClient('/legacy-ball/orders', {
        method: 'POST',
        body: {
          referenceId: form.referenceId.trim(),
          purchaserName: form.purchaserName.trim(),
          purchaserEmail: form.purchaserEmail.trim(),
          purchaserPhone: form.purchaserPhone.trim(),
          ticketCount: parseInt(form.ticketCount, 10),
          tierId: form.tierId,
          ...(chapterAffiliation ? { chapterAffiliation } : {}),
          ...(form.isComplimentary ? { isComplimentary: true } : {}),
          ...(form.isComplimentary && form.complimentaryReason.trim() ? { complimentaryReason: form.complimentaryReason.trim() } : {}),
        },
      });

      setSuccessMessage('Order created successfully.');
      setForm(initialFormState);
      onOrderCreated();
    } catch (err: unknown) {
      const apiErr = err as { statusCode?: number; message?: string };
      if (apiErr?.statusCode === 409) {
        setApiError('An order with this reference ID already exists.');
      } else if (apiErr?.statusCode === 400) {
        setApiError(apiErr.message || 'Validation error. Please check your inputs.');
      } else {
        setApiError(apiErr?.message || 'Failed to create order. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={styles.formSection}>
      <h3 style={styles.formTitle}>Create Ticket Order</h3>
      <form onSubmit={handleSubmit}>
        <div style={styles.formGrid}>
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>Reference ID</label>
            <input
              type="text"
              style={errors.referenceId ? styles.formInputError : styles.formInput}
              value={form.referenceId}
              onChange={handleChange('referenceId')}
              placeholder="e.g. ORD-001"
              disabled={submitting}
            />
            {errors.referenceId && (
              <span style={styles.fieldError}>{errors.referenceId}</span>
            )}
          </div>

          <div style={styles.formGroup}>
            <label style={styles.formLabel}>Purchaser Name</label>
            <input
              type="text"
              style={errors.purchaserName ? styles.formInputError : styles.formInput}
              value={form.purchaserName}
              onChange={handleChange('purchaserName')}
              placeholder="Full name"
              disabled={submitting}
            />
            {errors.purchaserName && (
              <span style={styles.fieldError}>{errors.purchaserName}</span>
            )}
          </div>

          <div style={styles.formGroup}>
            <label style={styles.formLabel}>Email</label>
            <input
              type="email"
              style={errors.purchaserEmail ? styles.formInputError : styles.formInput}
              value={form.purchaserEmail}
              onChange={handleChange('purchaserEmail')}
              placeholder="email@example.com"
              disabled={submitting}
            />
            {errors.purchaserEmail && (
              <span style={styles.fieldError}>{errors.purchaserEmail}</span>
            )}
          </div>

          <div style={styles.formGroup}>
            <label style={styles.formLabel}>Phone</label>
            <input
              type="tel"
              style={errors.purchaserPhone ? styles.formInputError : styles.formInput}
              value={form.purchaserPhone}
              onChange={handleChange('purchaserPhone')}
              placeholder="(555) 123-4567"
              disabled={submitting}
            />
            {errors.purchaserPhone && (
              <span style={styles.fieldError}>{errors.purchaserPhone}</span>
            )}
          </div>

          <div style={styles.formGroup}>
            <label style={styles.formLabel}>Ticket Count (1–100)</label>
            <input
              type="number"
              style={errors.ticketCount ? styles.formInputError : styles.formInput}
              value={form.ticketCount}
              onChange={handleChange('ticketCount')}
              min={1}
              max={100}
              placeholder="1"
              disabled={submitting}
            />
            {errors.ticketCount && (
              <span style={styles.fieldError}>{errors.ticketCount}</span>
            )}
          </div>

          <div style={styles.formGroup}>
            <label style={styles.formLabel}>Tier</label>
            <select
              style={errors.tierId ? styles.formSelectError : styles.formSelect}
              value={form.tierId}
              onChange={handleChange('tierId')}
              disabled={submitting}
            >
              <option value="">Select a tier...</option>
              {activeTiers.map((tier) => (
                <option key={tier.id} value={tier.id}>
                  {tier.name} ({formatCurrency(tier.price)})
                </option>
              ))}
            </select>
            {errors.tierId && (
              <span style={styles.fieldError}>{errors.tierId}</span>
            )}
          </div>

          <div style={styles.formGroup}>
            <label style={styles.formLabel}>Chapter (Optional)</label>
            <ChapterDropdown
              value={form.chapter}
              freeText={form.chapterFreeText}
              onChange={(chapter) =>
                setForm((prev) => ({ ...prev, chapter }))
              }
              onFreeTextChange={(chapterFreeText) =>
                setForm((prev) => ({ ...prev, chapterFreeText }))
              }
              disabled={submitting}
            />
          </div>
        </div>

        {/* Complimentary Order */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={form.isComplimentary}
              onChange={(e) => setForm((prev) => ({ ...prev, isComplimentary: e.target.checked }))}
              disabled={submitting}
            />
            🎁 Complimentary (no revenue)
          </label>
        </div>
        {form.isComplimentary && (
          <div style={{ ...styles.formGroup, marginBottom: '0.75rem', maxWidth: '400px' }}>
            <label style={styles.formLabel}>Reason (optional)</label>
            <input
              type="text"
              style={styles.formInput}
              value={form.complimentaryReason}
              onChange={(e) => setForm((prev) => ({ ...prev, complimentaryReason: e.target.value }))}
              placeholder="e.g. VIP Guest, Speaker, Sponsor"
              disabled={submitting}
            />
          </div>
        )}

        <button
          type="submit"
          style={submitting ? styles.formButtonDisabled : styles.formButton}
          disabled={submitting}
        >
          {submitting ? 'Creating Order...' : 'Create Order'}
        </button>
      </form>

      {successMessage && (
        <div style={styles.successMessage}>{successMessage}</div>
      )}
      {apiError && <div style={styles.formError}>{apiError}</div>}
    </div>
  );
}
